import React, { useState } from 'react';
import { MessageCircle, Loader2 } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { IconButton } from './UI';

const ChatWidget: React.FC = () => {
  const { settings } = useSettings();
  const chatWidget = settings?.chatWidget;

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    { id: string; from: 'user' | 'bot'; text: string; timestamp: string }[]
  >([]);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const webhookUrl = chatWidget?.webhookUrl?.trim();
    if (!webhookUrl) {
      setError('Chat temporariamente indisponível. Tente novamente em instantes.');
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      from: 'user' as const,
      text: trimmed,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setSending(true);
    setError(null);

    try {
      const history = messages.map((m) => ({
        from: m.from,
        text: m.text,
        timestamp: m.timestamp,
      }));

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: trimmed,
          history,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));
      const replyText: string =
        data.reply || data.message || data.text || 'Desculpe, não consegui entender a resposta da IA.';

      const botMessage = {
        id: `bot-${Date.now()}`,
        from: 'bot' as const,
        text: replyText,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.error('Erro ao enviar mensagem para o webhook do chat:', err);
      setError('Não foi possível enviar a mensagem. Tente novamente em instantes.');
    } finally {
      setSending(false);
    }
  };

  if (!chatWidget?.enabled || !chatWidget?.webhookUrl?.trim()) {
    return null;
  }

  return (
    <>
      {/* Janela de chat */}
      {isOpen && (
        <div className="fixed bottom-[9.5rem] right-4 sm:right-6 lg:bottom-24 z-[9999] w-80 max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
            <div>
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <h2 className="text-sm font-semibold">{chatWidget?.title || 'Atendimento NEIA'}</h2>
              </div>
            </div>
            <IconButton action="close" label="Fechar chat" onClick={toggleOpen} />
          </div>

          <div className="flex-1 px-4 py-3 space-y-2 overflow-y-auto max-h-80 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-xs text-gray-500 text-center px-2">
                Olá! Envie sua pergunta e a IA da NEIA responderá aqui mesmo.
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`px-3 py-2 rounded-2xl text-xs max-w-[80%] shadow-sm ${
                    msg.from === 'user'
                      ? 'bg-primary-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                {chatWidget?.typingLabel || 'IA digitando...'}
              </div>
            )}
            {error && (
              <div className="text-[11px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-2 py-1 mt-1">
                {error}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-white px-3 py-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="flex-1 text-xs px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Digite sua mensagem..."
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed hover:bg-primary-700 transition-colors"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <MessageCircle className="w-4 h-4" />
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Botão flutuante */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-label="Abrir chat"
        className="fixed bottom-20 right-4 sm:right-6 lg:bottom-6 z-[9999] w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-primary-300 hover:bg-primary-700 hover:scale-110 transition-transform cursor-pointer chat-widget-float"
      >
        <MessageCircle className="w-7 h-7" strokeWidth={2} />
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
        </span>
      </button>

      <style>{`
        .chat-widget-float {
          animation: chat-float 2s ease-in-out infinite;
        }
        @keyframes chat-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
